// src/components/Admin/Cantieri/RapportinoDetail.jsx
import React from 'react';
import { calculateRapportinoTotals } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import './RapportinoDetail.css';

const RapportinoDetail = ({ 
  rapportino, 
  cantiere, 
  onBack, 
  onEdit, 
  onDuplicate, 
  onDelete 
}) => {
  const { currentUser } = useAuth();

  const formatDate = (date) => {
    if (!date) return 'Data non disponibile';
    try {
      return new Date(date).toLocaleDateString('it-IT', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Data non valida';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const totali = calculateRapportinoTotals(rapportino);

  return (
    <div className="rapportino-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            ← Torna alla Lista
          </button>
          <div className="header-info">
            <h1>📝 Rapportino del {formatDate(rapportino.data)}</h1>
            <p className="cantiere-info">
              <strong>Cantiere:</strong> {cantiere.nomeCommessa}
            </p>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => onEdit(rapportino)}
          >
            ✏️ Modifica
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => onDuplicate(rapportino)}
          >
            📋 Duplica
          </button>
          <button 
            className="btn btn-danger"
            onClick={() => onDelete(rapportino)}
          >
            🗑️ Elimina
          </button>
        </div>
      </div>

      {/* Contenuto principale */}
      <div className="detail-content">
        {/* Note varie in evidenza se presenti */}
        {rapportino.noteVarie && (
          <div className="note-section">
            <h3>📝 Note Varie</h3>
            <div className="note-content">
              {rapportino.noteVarie}
            </div>
          </div>
        )}

        {/* Sezioni principali */}
        <div className="sections-container">
          {/* Sezione Personale */}
          <div className="detail-section">
            <h3>👷 Personale Presente</h3>
            {rapportino.personalePresente && rapportino.personalePresente.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.personalePresente.map((persona, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{persona.nome}</strong>
                        <span className="item-details">
                          {persona.oreLavorate}h × {formatCurrency(persona.costoOrario)}/h
                        </span>
                      </div>
                      <div className="item-total">
                        {formatCurrency(persona.oreLavorate * persona.costoOrario)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="section-total">
                  <strong>Totale Personale: {formatCurrency(totali.totalePersonale)}</strong>
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun personale registrato</p>
              </div>
            )}
          </div>

          {/* Sezione Mezzi */}
          <div className="detail-section">
            <h3>🚛 Mezzi Utilizzati</h3>
            {rapportino.mezziUtilizzati && rapportino.mezziUtilizzati.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.mezziUtilizzati.map((mezzo, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{mezzo.nome}</strong>
                        <span className="item-details">
                          {mezzo.quantita} × {formatCurrency(mezzo.costoUnitario)}
                        </span>
                      </div>
                      <div className="item-total">
                        {formatCurrency(mezzo.totale)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="section-total">
                  <strong>Totale Mezzi: {formatCurrency(totali.totaleMezzi)}</strong>
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun mezzo utilizzato</p>
              </div>
            )}
          </div>

          {/* Sezione Materiali Variabili */}
          <div className="detail-section">
            <h3>📦 Materiali a Costo Variabile</h3>
            {rapportino.materialiVariabili && rapportino.materialiVariabili.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.materialiVariabili.map((materiale, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{materiale.nome}</strong>
                        <span className="item-details">
                          {materiale.quantita} {materiale.unita}
                        </span>
                      </div>
                      <div className="item-total">
                        {formatCurrency(materiale.costo)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="section-total">
                  <strong>Totale Mat. Variabili: {formatCurrency(totali.totaleMatVariabili)}</strong>
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun materiale a costo variabile</p>
              </div>
            )}
          </div>

          {/* Sezione Materiali Fissi */}
          <div className="detail-section">
            <h3>🏗️ Materiali a Costo Fisso</h3>
            {rapportino.materialiFissi && rapportino.materialiFissi.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.materialiFissi.map((materiale, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{materiale.nome}</strong>
                        <span className="item-details">
                          {materiale.quantita} {materiale.unita}
                        </span>
                      </div>
                      <div className="item-total">
                        {formatCurrency(materiale.totale)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="section-total">
                  <strong>Totale Mat. Fissi: {formatCurrency(totali.totaleMatFissi)}</strong>
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun materiale a costo fisso</p>
              </div>
            )}
          </div>

          {/* Sezione Lavorazioni */}
          <div className="detail-section">
            <h3>⚒️ Lavorazioni</h3>
            {rapportino.lavorazioni && rapportino.lavorazioni.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.lavorazioni.map((lavorazione, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{lavorazione.nome}</strong>
                        <span className="item-details">
                          {lavorazione.quantita} {lavorazione.unita}
                        </span>
                      </div>
                      <div className="item-note">
                        {lavorazione.note && <em>{lavorazione.note}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessuna lavorazione registrata</p>
              </div>
            )}
          </div>

          {/* Sezione Ricavi */}
          <div className="detail-section">
            <h3>💰 Ricavi</h3>
            {rapportino.ricavi && rapportino.ricavi.length > 0 ? (
              <div className="section-content">
                <div className="items-list">
                  {rapportino.ricavi.map((ricavo, index) => (
                    <div key={index} className="item-row">
                      <div className="item-info">
                        <strong>{ricavo.descrizione}</strong>
                        {ricavo.note && (
                          <span className="item-details">{ricavo.note}</span>
                        )}
                      </div>
                      <div className="item-total revenue">
                        {formatCurrency(ricavo.importo)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="section-total revenue">
                  <strong>Totale Ricavi: {formatCurrency(totali.totaleRicavi)}</strong>
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun ricavo registrato</p>
              </div>
            )}
          </div>

          {/* Sezione Allegati */}
          <div className="detail-section">
            <h3>📎 Allegati</h3>
            {rapportino.allegati && rapportino.allegati.length > 0 ? (
              <div className="section-content">
                <div className="attachments-grid">
                  {rapportino.allegati.map((allegato, index) => (
                    <div key={index} className="attachment-item">
                      <div className="attachment-icon">
                        {allegato.type?.startsWith('image/') ? '🖼️' : '📄'}
                      </div>
                      <div className="attachment-info">
                        <strong>{allegato.name}</strong>
                        <span className="attachment-size">
                          {allegato.size ? `${(allegato.size / 1024 / 1024).toFixed(2)} MB` : ''}
                        </span>
                      </div>
                      <a 
                        href={allegato.url} 
                        download={allegato.name}
                        className="attachment-download"
                        title="Scarica"
                      >
                        📥
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-section">
                <p>Nessun allegato presente</p>
              </div>
            )}
          </div>

          {/* Riepilogo Economico */}
          <div className="detail-section economic-summary">
            <h3>📊 Riepilogo Economico</h3>
            <div className="section-content">
              <div className="economic-grid">
                <div className="economic-item">
                  <span className="economic-label">Totale Personale:</span>
                  <span className="economic-value">{formatCurrency(totali.totalePersonale)}</span>
                </div>
                <div className="economic-item">
                  <span className="economic-label">Totale Mezzi:</span>
                  <span className="economic-value">{formatCurrency(totali.totaleMezzi)}</span>
                </div>
                <div className="economic-item">
                  <span className="economic-label">Totale Materiali Variabili:</span>
                  <span className="economic-value">{formatCurrency(totali.totaleMatVariabili)}</span>
                </div>
                <div className="economic-item">
                  <span className="economic-label">Totale Materiali Fissi:</span>
                  <span className="economic-value">{formatCurrency(totali.totaleMatFissi)}</span>
                </div>
                <div className="economic-item total-costs">
                  <span className="economic-label">TOTALE COSTI:</span>
                  <span className="economic-value">{formatCurrency(totali.totaleCosti)}</span>
                </div>
                <div className="economic-item total-revenue">
                  <span className="economic-label">TOTALE RICAVI:</span>
                  <span className="economic-value">{formatCurrency(totali.totaleRicavi)}</span>
                </div>
                <div className={`economic-item total-margin ${totali.margine >= 0 ? 'positive' : 'negative'}`}>
                  <span className="economic-label">MARGINE:</span>
                  <span className="economic-value">{formatCurrency(totali.margine)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Modifiche */}
          <div className="detail-section">
            <h3>ℹ️ Informazioni</h3>
            <div className="section-content">
              <div className="info-grid">
                <div className="info-item">
                  <strong>Creato il:</strong>
                  <span>{rapportino.createdAt ? 
                    new Date(rapportino.createdAt.toDate?.() || rapportino.createdAt).toLocaleString('it-IT') : 
                    'N/A'
                  }</span>
                </div>
                {rapportino.updatedAt && (
                  <div className="info-item">
                    <strong>Ultima modifica:</strong>
                    <span>{new Date(rapportino.updatedAt.toDate?.() || rapportino.updatedAt).toLocaleString('it-IT')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RapportinoDetail;